import { Component, OnInit } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from "primeng/dynamicdialog";
import {
  AsReportedSecurityBreakdown,
  AsReportedSecurityBreakdownTags,
} from "src/api/models";
import { FilingService, TradingPlanService } from "src/api/services";
import { ViewSecurityTagComponent } from "../view-security-tag/view-security-tag.component";
import { AddTextComponent } from "../add-text/add-text.component";

@Component({
  selector: "app-processed-view",
  // imports: [],
  templateUrl: "./processed-view.component.html",
  styleUrl: "./processed-view.component.css",
  standalone: false,
})
export class ProcessedViewComponent implements OnInit {
  first = 0;
  rows = 10;
  tradings!: any;
  expandedRows: any = {};
  tradingPlansIdsForDeletion: number[] = [];
  allowedUsers = [
    'muhammad.ali@2iqresearch.com',
    'mansub.niaz@2iqresearch.com',
    'usman.naveed@2iqresearch.com',
    'hamza.saleem@2iqresearch.com'
  ];
  userEmail: string | null = null;
  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private _filingService: FilingService,
    private _confirmationService: ConfirmationService,
    private _tradingPlanService: TradingPlanService,
    public dialogService: DialogService,
    private _messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.tradings = this.config.data.filing;
    this.calculateExpirationDatesOnAdoption();
    this.userEmail = localStorage.getItem('email');
  }

  isAllowedUser(): boolean {
    return this.userEmail ? this.allowedUsers.includes(this.userEmail.toLowerCase()) : false;
  }

  ngAfterViewInit(): void {}

  next() {
    this.first = this.first + this.rows;
  }

  prev() {
    this.first = this.first - this.rows;
  }

  reset() {
    this.first = 0;
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  isLastPage(): boolean {
    return this.tradings
      ? this.first + this.rows >= this.tradings.length
      : true;
  }

  isFirstPage(): boolean {
    return this.tradings ? this.first === 0 : true;
  }

  onRowExpand(event: any) {
    this.expandedRows[event.data.tradingPlanId] = true;
  }

  onRowCollapse(event: any) {
    delete this.expandedRows[event.data.tradingPlanId];
  }

  // Change from array to single object
  selectedPlan: any = null;

  // Update the onSelectionChange method
  onSelectionChange(event: any) {
    this.selectedPlan = event;
  }

  // Update the onEditClick method
  onEditClick() {
    if (this.selectedPlan) {
      this.ref.close([this.selectedPlan]);
    }
  }

  onCheckboxChange(tradingPlanId: number, isChecked: boolean): void {
    if (isChecked) {
      if (!this.tradingPlansIdsForDeletion.includes(tradingPlanId)) {
        this.tradingPlansIdsForDeletion.push(tradingPlanId);
      }
    } else {
      this.tradingPlansIdsForDeletion = this.tradingPlansIdsForDeletion.filter(
        (id) => id !== tradingPlanId
      );
    }
  }

  onDeleteSelectedPlans(): void {
    if (this.tradingPlansIdsForDeletion.length === 0) return;

    this._confirmationService.confirm({
      key: "trading-confirm",
      message: "Are you sure you want to delete the selected trading plans?",
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        const payload = {
          tradingPlansIds: this.tradingPlansIdsForDeletion,
          reason: "Incorrect plan created by LLM",
        };

        this._tradingPlanService.deleteTradingPlans(payload).subscribe({
          next: () => {
            if (!this.tradings.tradingPlans) return;
            this.tradings.tradingPlans = this.tradings.tradingPlans.filter(
              (plan: any) =>
                !this.tradingPlansIdsForDeletion.includes(
                  plan.tradingPlanId as any
                )
            );
            this.tradingPlansIdsForDeletion = [];

            this._messageService.add({
              severity: "success",
              summary: "Success",
              detail: "Trading plans deleted successfully",
            });
            this._confirmationService.close();
          },
          error: () => {
            this._messageService.add({
              severity: "error",
              summary: "Error",
              detail: "Failed to delete trading plans",
            });
          },
        });
      },
    });
  }

  getStringForTransactionTags(
    securityBreakDownTags: AsReportedSecurityBreakdownTags | null
  ): string {
    if (!securityBreakDownTags) {
      return "";
    }
    return (
      [
        ["awardRelated", "A"],
        ["optionsRelated", "O"],
        ["giftRelated", "G"],
        ["taxWithheld", "T"],
        ["remuneration", "R"],
        ["isIndeterminable", "I"],
        ["sellToCover", "S"],

      ] as [keyof AsReportedSecurityBreakdownTags, string][]
    )
      .filter(([key]) => securityBreakDownTags[key])
      .map(([_, letter]) => letter)
      .join(" ");
  }
  writeText(trading: any): void {
    // Parse modificationComment if it's a JSON string
    let parsedComment: any = null;
    let commentText = "";
    let modifiedFields = trading.modifiedFields;
    let isEstimatedAdoptionDate = trading.isEstimatedAdoptionDate || trading.is_estimated_adoption_date;
    let isEstimatedTerminationDate = trading.isEstimatedTerminationDate || trading.is_estimated_termination_date;
    let isEstimatedAmendedDate = trading.isEstimatedAmendedDate || trading.is_estimated_amended_date;

    if (trading.modificationComment) {
      try {
        parsedComment = JSON.parse(trading.modificationComment);
        if (parsedComment && typeof parsedComment === "object") {
          commentText = parsedComment.comment || "";
          modifiedFields = parsedComment.modifiedFields || modifiedFields;
          isEstimatedAdoptionDate = parsedComment.is_estimated_adoption_date !== undefined
            ? parsedComment.is_estimated_adoption_date
            : isEstimatedAdoptionDate;
          isEstimatedTerminationDate = parsedComment.is_estimated_termination_date !== undefined
            ? parsedComment.is_estimated_termination_date
            : isEstimatedTerminationDate;
          isEstimatedAmendedDate = parsedComment.is_estimated_amended_date !== undefined
            ? parsedComment.is_estimated_amended_date
            : isEstimatedAmendedDate;
        } else {
          commentText = trading.modificationComment;
        }
      } catch {
        // If parsing fails, treat as plain text
        commentText = trading.modificationComment || "";
      }
    }

    const ref = this.dialogService.open(AddTextComponent, {
      width: "30vw",
      modal: true,
      closable: true,
      header: "View Tag",
      data: {
        modificationComment: commentText,
        modifiedFields: modifiedFields,
        isEstimatedAdoptionDate: isEstimatedAdoptionDate,
        isEstimatedTerminationDate: isEstimatedTerminationDate,
        isEstimatedAmendedDate: isEstimatedAmendedDate,
        tradingPlanId: trading.tradingPlanId || trading.id,
        processed: true,
      },
    });

    ref.onClose.subscribe((payload: string) => {
      if (!payload) return;

      trading.modificationComment = payload;

      try {
        const parsed = JSON.parse(payload);
        trading.modifiedFields = parsed?.modifiedFields ?? null;
        trading.is_estimated_adoption_date = parsed?.is_estimated_adoption_date ?? null;
        trading.is_estimated_termination_date =
          parsed?.is_estimated_termination_date ?? null;
        trading.is_estimated_amended_date =
          parsed?.is_estimated_amended_date ?? null;
      } catch {
        trading.modifiedFields = null;
        trading.is_estimated_adoption_date = null;
        trading.is_estimated_termination_date = null;
        trading.is_estimated_amended_date = null;
      }
    });
  }
  calculateExpirationDatesOnAdoption(): void {
    if (!this.tradings?.tradingPlans) return;

    this.tradings.tradingPlans.forEach((plan: any) => {
      // Only calculate when action = 'adoption'
      if (plan.action === "ADOPTION") return;

      // Skip if expiration date already exists
      if (plan.expirationDate) return;

      const adoptionDate = plan.adoptionDate
        ? new Date(plan.adoptionDate)
        : null;
      const duration = plan.durationDays ? Number(plan.durationDays) : NaN;

      // Only calculate if both are valid
      if (adoptionDate && !isNaN(duration)) {
        const expiration = new Date(adoptionDate);
        expiration.setDate(adoptionDate.getDate() + duration);
        plan.expirationDate = this.formatToDateString(expiration) || undefined;
      }
    });
  }

  // Helper function for formatting date (same as in other component)
  private formatToDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  discardFiling(filing: any): void {
    this._confirmationService.confirm({
      key: "discardFilingForProcessedView",
      message: `Are you sure you want to discard filing #${filing.filingId}?`,
      header: "Confirm Discard",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this._filingService
          .discardFiling({
            filingId: filing.filingId,
            reason: "no plan found",
          })
          .subscribe({
            next: () => {
              this._messageService.add({
                severity: "success",
                summary: "Discarded",
                detail: `Filing #${filing.filingId} discarded successfully.`,
              });
            },
            error: (err) => {
              this._messageService.add({
                severity: "error",
                summary: "Error",
                detail: err?.error?.message || "Failed to discard filing.",
              });
            },
            complete: () => {
              this.ref.close({ refresh: true });
            },
          });
      },
      reject: () => {
        this._messageService.add({
          severity: "info",
          summary: "Cancelled",
          detail: "Discard action was cancelled.",
        });
      },
    });
  }
}
